import React from "react";
import ReactDOM from "react-dom/client";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { LogIn } from "lucide-react";
import "./index.css";
import { router, publicRouter } from "./router";
import { ThemeProvider } from "./components/theme";
import { SpaceProvider } from "./api/space-context";

// Preserva o path (ex.: /invite/:token) através do fluxo de login do Auth0.
function onRedirectCallback(appState?: { returnTo?: string }) {
  const target = appState?.returnTo;
  if (target && target !== window.location.pathname) {
    window.history.replaceState({}, "", target);
  }
}

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function Gate() {
  const { isLoading, isAuthenticated, error, loginWithRedirect } = useAuth0();

  // Link público anônimo: pula o login e serve o router mínimo (sem sidebar).
  // O acesso é validado pelo token no worker, não pelo JWT.
  if (window.location.pathname.startsWith("/s/")) {
    return <RouterProvider router={publicRouter} />;
  }

  if (isLoading) return <FullScreen>Carregando…</FullScreen>;
  if (error) return <FullScreen>Erro de login: {error.message}</FullScreen>;
  if (!isAuthenticated) {
    // Deep-link (ex.: convite): loga já preservando o destino.
    const returnTo = window.location.pathname;
    const deepLink = returnTo.startsWith("/invite/");
    return (
      <FullScreen>
        <div className="flex flex-col items-center gap-4">
          <img src="/etus-logo.png" alt="ETUS" className="h-16 w-16 rounded-2xl shadow-sm" />
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">ETUS Mindmap</h1>
          {deepLink && (
            <p className="text-sm text-slate-500 dark:text-slate-400">Entre para ver seu convite.</p>
          )}
          <button
            type="button"
            onClick={() => loginWithRedirect(deepLink ? { appState: { returnTo } } : undefined)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark"
          >
            <LogIn className="h-4 w-4" aria-hidden />
            Entrar
          </button>
        </div>
      </FullScreen>
    );
  }
  return <RouterProvider router={router} />;
}

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-700 dark:bg-slate-950 dark:text-slate-300">
      {children}
    </div>
  );
}

const root = document.getElementById("root")!;
ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <Auth0Provider
      domain={import.meta.env.VITE_AUTH0_DOMAIN}
      clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        // offline_access = refresh token: renova pelo /token (sem iframe), o que
        // evita o `consent_required` que o Chrome dispara ao bloquear cookies de
        // terceiros na renovação silenciosa via iframe.
        scope: "openid profile email offline_access",
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens
      useRefreshTokensFallback
    >
      <ThemeProvider>
        <QueryClientProvider client={qc}>
          <SpaceProvider>
            <Gate />
          </SpaceProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </Auth0Provider>
  </React.StrictMode>,
);
